declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signupDemoSchema, type SignupDemoInput } from '@daater/shared';
import { Button } from '@daater/ui';
import { useRegisterUserMutation } from '../api/queries';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';

export default function SignupDemoScreen() {
  const navigate = useNavigate();
  const registerMutation = useRegisterUserMutation();
  const [submitted, setSubmitted] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupDemoInput>({
    resolver: zodResolver(signupDemoSchema),
  });

  const onSubmit = async (values: SignupDemoInput) => {
    setErrorMessage(null);
    try {
      await registerMutation.mutateAsync({
        fullName: values.fullName,
        phone: values.phone,
        email: values.email,
        password: values.password,
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'registrationComplete' });

      setSubmitted(true);
    } catch (error) {
      const message = (error as Error).message;
      setErrorMessage(message || 'Error al registrar. Intenta de nuevo.');
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden bg-[#1a2e58] px-12 text-white lg:flex lg:w-1/2 lg:flex-col lg:items-center lg:justify-center">
        <div className="mx-auto max-w-md space-y-8">
          <DaaterLogoHomeLink
            alt="Daater"
            className="inline-flex w-fit"
            imgClassName="h-12 brightness-0 invert"
          />
          <blockquote className="border-l-2 border-white/30 pl-5 text-lg leading-relaxed text-white/80">
            Inteligencia de datos de comercio exterior al alcance de tu equipo.
          </blockquote>
          <p className="text-sm text-white/50">
            Regístrate para solicitar una demo y conocer todo lo que Daater puede hacer por tu
            empresa.
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-pale-grey px-4 lg:w-1/2">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          <div className="mb-6 flex justify-center lg:hidden">
            <DaaterLogoHomeLink alt="Daater" imgClassName="h-10" />
          </div>

          <h1 className="text-2xl font-semibold text-charcoal-grey">Solicitar demo</h1>
          <p className="mt-1 text-sm text-slate">
            Completa el formulario para solicitar acceso a Daater.
          </p>

          {submitted ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-md border border-greenish-teal/30 bg-greenish-teal/10 px-4 py-4 text-sm">
                <p className="font-semibold text-charcoal-grey">¡Gracias por registrarte!</p>
                <p className="mt-2 text-slate">
                  En breve un asesor comercial de nuestro equipo lo contactará para habilitar su
                  cuenta.
                </p>
                <p className="mt-2 text-slate">
                  Recuerda que nuestros números de contacto son: +57 (2) 488 2254 / +57 3016359017 /
                  +57 3042018256 y nuestro correo es{' '}
                  <a href="mailto:soporte@daater.com" className="text-primary underline">
                    soporte@daater.com
                  </a>
                </p>
                <p className="mt-2 text-slate">Atentamente,</p>
                <p className="text-slate">Equipo de Daater</p>
              </div>
              <button
                type="button"
                onClick={() => void navigate({ to: '/login' })}
                className="text-xs text-slate underline"
              >
                Regresar
              </button>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={event => void handleSubmit(onSubmit)(event)}>
              <div>
                <label className="text-xs font-semibold uppercase text-slate">
                  Nombre completo
                </label>
                <input
                  type="text"
                  {...register('fullName')}
                  className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="Juan Pérez"
                />
                {errors.fullName ? (
                  <p className="mt-1 text-xs text-darkish-pink">{errors.fullName.message}</p>
                ) : null}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-slate">Correo</label>
                <input
                  type="email"
                  {...register('email')}
                  className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="correo@empresa.com"
                />
                {errors.email ? (
                  <p className="mt-1 text-xs text-darkish-pink">{errors.email.message}</p>
                ) : null}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-slate">Teléfono</label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="+57 300 1234567"
                />
                {errors.phone ? (
                  <p className="mt-1 text-xs text-darkish-pink">{errors.phone.message}</p>
                ) : null}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-slate">Contraseña</label>
                <input
                  type="password"
                  {...register('password')}
                  className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="••••••••"
                />
                {errors.password ? (
                  <p className="mt-1 text-xs text-darkish-pink">{errors.password.message}</p>
                ) : null}
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  {...register('terms')}
                  id="terms"
                  className="mt-1 h-4 w-4 rounded border-pale-grey-three"
                />
                <label htmlFor="terms" className="text-xs text-slate">
                  Acepto los{' '}
                  <a
                    href="https://daater.com/terminos-y-condiciones/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    términos y condiciones
                  </a>
                </label>
              </div>
              {errors.terms ? (
                <p className="text-xs text-darkish-pink">{errors.terms.message}</p>
              ) : null}

              {errorMessage ? (
                <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                  {errorMessage}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
                {registerMutation.isPending ? 'Registrando...' : 'Registrarme'}
              </Button>
            </form>
          )}

          {!submitted ? (
            <div className="mt-4 flex items-center justify-between text-xs text-slate">
              <button
                type="button"
                onClick={() => void navigate({ to: '/login' })}
                className="underline"
              >
                Ya tengo cuenta
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
